import { Injectable } from '@angular/core';
import { DictionaryService } from '@app/core/services/dictionary.service';
import { UserService } from '@app/core/services/user.service';
import { Agency } from '@app/models';
import { AdvancedSearchActionTypes } from '@app/pcs/actions/advanced-search';
import * as AdvancedSearchActions from '@app/pcs/actions/advanced-search';
import { processTypeControl } from '@app/pcs/effects/advanced-search.effects';
import {
  hasSelectedAccepted,
  InvolvedAgencyTrafficLight,
  ProcessType,
  ProgressStatus
} from '@app/pcs/models';
import * as fromAdvancedSearch from '@app/pcs/reducer/advanced-search';
import {
  ADVANCED_SEARCH_IM_PERMANENT_ACCEPTANCE,
  ADVANCED_SEARCH_INVOLVED_AGENCY_TRAFFIC_LIGHT,
  ADVANCED_SEARCH_MY_PROGRESS_STATUSES, IM_PERMANENT_ACCEPTANCE, PROGRESS_STATUS
} from '@app/pcs/reducer/advanced-search/search-form';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { hasNoValue, mapIndexed, mapPayload } from '@util';
import { DisableAction, EnableAction, SetValueAction } from 'ngrx-forms';
import * as R from 'ramda';
import { filter, switchMap, withLatestFrom } from 'rxjs/operators';

const permanentImAcceptanceInInvolvedAgencyControl = (controlId: string) =>
  controlId.includes(ADVANCED_SEARCH_INVOLVED_AGENCY_TRAFFIC_LIGHT) &&
  (controlId.includes(PROGRESS_STATUS) || controlId.includes('agency'));

const permanentImAcceptanceControl = (controlId: string) => controlId.includes(ADVANCED_SEARCH_MY_PROGRESS_STATUSES);

const disableActions = (controlId: string) => [new SetValueAction(controlId, null), new DisableAction(controlId)];

@Injectable()
export class PermanentImAcceptanceSearchEffects {

  private myProgressStatuses$ = this.store.pipe(select(fromAdvancedSearch.getMyProgressStatuses));
  private involvedAgencyTrafficLightPairs$ = this.store.pipe(select(fromAdvancedSearch.getInvolvedAgencyTrafficLightPairs));
  private processType$ = this.store.pipe(select(fromAdvancedSearch.getSelectedProcessType));

  handleImPermanentAcceptanceFieldsOnInitAdvancedForm$ = createEffect(() => this.actions$.pipe(
    ofType<AdvancedSearchActions.InitAdvancedSearchForm>(AdvancedSearchActionTypes.InitAdvancedSearchForm),
    mapPayload(),
    withLatestFrom(this.myProgressStatuses$, this.involvedAgencyTrafficLightPairs$, this.processType$),
    switchMap(([_, myProgressStatuses, involvedAgencyTrafficLightPairs, processType]) => [
      ...this.handleImPermanentAcceptanceByUser(myProgressStatuses, processType, ADVANCED_SEARCH_IM_PERMANENT_ACCEPTANCE, this.userService.isImUser()),
      ...this.handleImPermanentAcceptanceInInvolvedAgency(involvedAgencyTrafficLightPairs, processType)
    ])
  ));

  handleImPermanentAcceptanceField$ = createEffect(() => this.actions$.pipe(
    ofType<SetValueAction<ProgressStatus[] | ProcessType>>(SetValueAction.TYPE),
    filter(({controlId}) => permanentImAcceptanceControl(controlId) || processTypeControl(controlId)),
    withLatestFrom(this.myProgressStatuses$, this.processType$),
    switchMap(([_, myProgressStatuses, processType]) =>
      [...this.handleImPermanentAcceptanceByUser(myProgressStatuses, processType, ADVANCED_SEARCH_IM_PERMANENT_ACCEPTANCE, this.userService.isImUser())])
  ));

  handleImPermanentAcceptanceFieldInInvolvedAgency$ = createEffect(() => this.actions$.pipe(
    ofType<SetValueAction<ProgressStatus | Agency | ProcessType>>(SetValueAction.TYPE),
    filter(({controlId}) => permanentImAcceptanceInInvolvedAgencyControl(controlId) || processTypeControl(controlId)),
    withLatestFrom(this.involvedAgencyTrafficLightPairs$, this.processType$),
    switchMap(([_, involvedAgencyTrafficLightPairs, processType]) =>
      [...this.handleImPermanentAcceptanceInInvolvedAgency(involvedAgencyTrafficLightPairs, processType)])
  ));

  private handleImPermanentAcceptanceInInvolvedAgency = (involvedAgencyTrafficLightPairs: InvolvedAgencyTrafficLight[], processType: ProcessType): Action[] => {
    const resolveActions = (pair: InvolvedAgencyTrafficLight, index: number) => {
      const controlId = `${ADVANCED_SEARCH_INVOLVED_AGENCY_TRAFFIC_LIGHT}.${index}.${IM_PERMANENT_ACCEPTANCE}`;

      return this.handleImPermanentAcceptanceByUser([pair.progressStatus], processType, controlId, pair?.agency?.im,
        disableActions(controlId));
    };

    return R.unnest(mapIndexed(resolveActions, involvedAgencyTrafficLightPairs));
  };

  private handleImPermanentAcceptanceByUser = (myProgressStatuses: ProgressStatus[],
    processType: ProcessType,
    controlId: string,
    isImUser: boolean,
    actions: Action[] = []) =>
    isImUser ? this.handleImPermanentAcceptance(myProgressStatuses, processType, controlId) : actions;

  private handleImPermanentAcceptance = (myProgressStatuses: ProgressStatus[],
    processType: ProcessType,
    controlId: string): Action[] => hasSelectedAccepted(myProgressStatuses) && this.processTypeEligibleForImPermanentAcceptance(processType)
    ? [new EnableAction(controlId)]
    : [...disableActions(controlId)];


  private processTypeEligibleForImPermanentAcceptance = (processType: ProcessType) =>
    hasNoValue(processType)
    || R.includes(processType, this.dictionaryService.getProcessTypesSupportingPermanentAcceptance());

  constructor(private store: Store<fromAdvancedSearch.State>,
    private userService: UserService,
    private dictionaryService: DictionaryService,
    private actions$: Actions) {
  }
}
